import {
    Compilation,
    Compiler,
    DebugPanel,
    Emulator,
    Entry,
    Feature,
    Frequency,
    FrequencySpecs,
    HelpPanel,
    RomInfo,
    SectionInfo
} from "emukit";
import { loadAsync } from "jszip";

import { GameBoyCore } from "../core";
import { Info } from "../lib/boytacean";
import info from "../package.json";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const require: any;

export const GAMES = [
    {
        name: "Pokémon Blue",
        path: require("../../../res/roms/game/Pokemon - Blue Version (USA, Europe) (SGB Enhanced).gb")
    },
    {
        name: "Pokémon Red",
        path: require("../../../res/roms/game/Pokemon - Red Version (USA, Europe) (SGB Enhanced).gb")
    },
    {
        name: "Pokémon Yellow FR",
        path: require("../../../res/roms/game/Pokemon - Version Jaune - Edition Speciale Pikachu (France) (CGB+SGB Enhanced).gb")
    },
    {
        name: "Pokémon Yellow US",
        path: require("../../../res/roms/game/Pokemon - Yellow Version - Special Pikachu Edition (USA, Europe) (CGB+SGB Enhanced).gb")
    },
    {
        name: "Super Mario Bros",
        path: require("../../../res/roms/game/Super Mario Bros. Deluxe (Europe) (Rev 2).gbc")
    },
    {
        name: "Zelda Awakening",
        path: require("../../../res/roms/game/The Legend of Zelda - New Awakening (hack).gbc")
    }
];

/**
 * Top level class that controls the emulator behaviour, adapting
 * the headless Game Boy core into the EmuKit emulator interface.
 */
export class GameboyEmulator extends GameBoyCore implements Emulator {
    constructor(extraSettings: Record<string, string | boolean> = {}) {
        super({
            wasmPath: require("../lib/boytacean_bg.wasm"),
            extraSettings: extraSettings
        });
    }

    async boot(options = {} as { romPath?: string; loadRom?: boolean }) {
        await super.boot(options);
    }

    async loadGame(path: string) {
        await this.boot({ loadRom: true, romPath: path });
    }

    get name(): string {
        return "Brounou";
    }

    get device(): Entry {
        return {
            text: Info.system(),
            url: "https://en.wikipedia.org/wiki/Game_Boy"
        };
    }

    get icon(): string | undefined {
        return require("../res/star.png");
    }

    get version(): Entry | undefined {
        return {
            text: Info.version() ?? info.version
        };
    }

    get repository(): Entry {
        return {
            text: "Brounou"
        };
    }

    get features(): Feature[] {
        return [Feature.KeyboardGB];
    }

    get sections(): SectionInfo[] {
        return [];
    }

    get help(): HelpPanel[] {
        return [];
    }

    get debug(): DebugPanel[] {
        return [];
    }

    get romExts(): string[] {
        return ["gb", "gbc", "zip"];
    }

    get stateExts(): string[] {
        return ["sav", ...Array.from({ length: 10 }, (_, i) => `s${i + 1}`)];
    }

    get romInfo(): RomInfo {
        return {
            name: this.romName ?? undefined,
            data: this.romData ?? undefined,
            size: this.romSize,
            extra: {
                bootRom: this.gameBoy?.boot_rom_s(),
                romType: this.cartridge?.rom_type_s(),
                romSize: this.cartridge?.rom_size_s(),
                ramSize: this.cartridge?.ram_size_s()
            }
        };
    }

    get frequencySpecs(): FrequencySpecs {
        return {
            unit: Frequency.MHz,
            delta: 400000,
            places: 2
        };
    }

    get compiler(): Compiler | null {
        if (!this.gameBoy) return null;
        return {
            name: Info.compiler(),
            version: Info.compiler_version()
        };
    }

    get compilation(): Compilation | null {
        if (!this.gameBoy) return null;
        return {
            date: Info.compilation_date(),
            time: Info.compilation_time()
        };
    }

    get wasmEngine(): string | null {
        if (!this.gameBoy) return null;
        return Info.wasm_engine() ?? null;
    }

    async buildRomData(file: File): Promise<Uint8Array> {
        const arrayBuffer = await file.arrayBuffer();
        let romData = new Uint8Array(
            arrayBuffer
        ) as Uint8Array<ArrayBufferLike>;

        if (file.name.endsWith(".zip")) {
            const zip = await loadAsync(romData);
            const firstFile = Object.values(zip.files)[0];
            romData = await firstFile.async("uint8array");
        }

        return romData;
    }
}

declare global {
    interface Window {
        emulator: GameboyEmulator;
    }
}
